// DLQ console (§11 `/dlq`): dead runs that exhausted retries. Operators can select rows and either
// requeue (re-enqueue a fresh manual run, resetting the dead state) or delete (purge). Bulk actions
// post the selected run IDs to the admin DLQ endpoints; the backlog count drives the overview alarm.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Api, ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { fmtTime } from "../lib/format";
import { Card, Empty, ErrorBox, Spinner } from "../components/ui";

export function DlqPage() {
  const { data, error, loading, reload } = useAsync(() => Api.listDlq({ limit: "100" }), []);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (!data) return;
    setSelected((s) => (s.size === data.data.length ? new Set() : new Set(data.data.map((r) => r.id))));
  }

  async function run(action: "requeue" | "delete") {
    if (selected.size === 0) return;
    setActionError(null);
    setNotice(null);
    setBusy(true);
    const ids = [...selected];
    try {
      if (action === "requeue") {
        const res = await Api.requeueDlq({ ids });
        setNotice(`Requeued ${res.requeued} run(s).`);
      } else {
        const res = await Api.deleteDlq({ ids });
        setNotice(`Deleted ${res.deleted} run(s).`);
      }
      setSelected(new Set());
      reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? new Error(err.message) : err instanceof Error ? err : new Error(String(err)),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Dead-letter queue</h1>
        <button className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      {actionError && <ErrorBox error={actionError} />}
      {notice && <div className="rounded border border-ok/40 bg-ok/10 p-2 text-sm text-ok">{notice}</div>}

      <Card>
        <div className="mb-3 flex items-center gap-2">
          <button className="btn btn-primary" disabled={busy || selected.size === 0} onClick={() => run("requeue")}>
            Requeue ({selected.size})
          </button>
          <button className="btn btn-danger" disabled={busy || selected.size === 0} onClick={() => run("delete")}>
            Delete ({selected.size})
          </button>
        </div>

        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorBox error={error} />
        ) : !data || data.data.length === 0 ? (
          <Empty>DLQ is empty.</Empty>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="th w-8">
                  <input
                    type="checkbox"
                    checked={selected.size === data.data.length && data.data.length > 0}
                    onChange={toggleAll}
                  />
                </th>
                <th className="th">Run</th>
                <th className="th">Task</th>
                <th className="th">Slot</th>
                <th className="th">Attempt</th>
                <th className="th">Error</th>
                <th className="th">Failed at</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((r) => (
                <tr key={r.id} className="hover:bg-bg/50">
                  <td className="td">
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td className="td">
                    <Link className="mono text-xs text-primary hover:underline" to={`/runs/${r.id}`}>
                      {r.id}
                    </Link>
                  </td>
                  <td className="td">
                    <Link className="mono text-xs text-primary hover:underline" to={`/tasks/${r.taskId}`}>
                      {r.taskId}
                    </Link>
                  </td>
                  <td className="td mono text-xs">{fmtTime(r.scheduledSlot)}</td>
                  <td className="td mono text-xs">{r.attempt}</td>
                  <td className="td mono text-xs text-danger">{r.error ?? "—"}</td>
                  <td className="td mono text-xs">{fmtTime(r.finishedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}